/**
 * Daily Completions Store
 *
 * Centralized in-memory cache of daily_completions rows keyed by (childId, date).
 * Designed as the single source of truth shared between the aggregated list (useAggregatedTasks) and
 * TasksOneKidPage so that swiping between views never refetches data already
 * loaded, and mutations in one view are immediately visible in the other.
 *
 * Phase A (current): batch read on tab mount, single read on demand, manual hydrate.
 * Phase B (future):  Supabase realtime listener feeding hydrate() on remote changes.
 * Phase C (future):  optimistic local-first writes with rollback.
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { dailyCompletionService } from '@/services/dailyCompletion.service';

function key(childId: string, date: string): string {
  return `${childId}:${date}`;
}

export const useDailyCompletionsStore = defineStore('dailyCompletions', () => {
  // State — Map<"childId:date", completion>. Using a ref<Map> + manual reassignment
  // for reactivity (Vue 3 tracks Map mutations via .set/.delete on the ref's value).
  const completions = ref<Map<string, any>>(new Map());
  // Tracks which (children, date) batches we've already loaded so we know when
  // a slide can be rendered with hydrated data vs needs a first-time fetch.
  const loadedDates = ref<Set<string>>(new Set());
  const loading = ref(false);

  // ── Getters ────────────────────────────────────────────────────────
  const get = computed(() => (childId: string, date: string) => {
    return completions.value.get(key(childId, date)) ?? null;
  });

  const has = computed(() => (childId: string, date: string) => {
    return completions.value.has(key(childId, date));
  });

  /** True when we've already done the initial batch fetch for this date. */
  function isDateLoaded(date: string): boolean {
    return loadedDates.value.has(date);
  }

  // ── Mutations ──────────────────────────────────────────────────────
  /**
   * Hydrate (or replace) a single completion in the cache.
   * Use from realtime listeners, single reads, or after a mutation succeeded.
   */
  function hydrate(completion: any): void {
    if (!completion?.child_id || !completion?.date) return;
    const next = new Map(completions.value);
    next.set(key(completion.child_id, completion.date), completion);
    completions.value = next;
  }

  function hydrateMany(rows: Iterable<any>): void {
    const next = new Map(completions.value);
    for (const row of rows) {
      if (row?.child_id && row?.date) {
        next.set(key(row.child_id, row.date), row);
      }
    }
    completions.value = next;
  }

  function remove(childId: string, date: string): void {
    const next = new Map(completions.value);
    next.delete(key(childId, date));
    completions.value = next;
  }

  function clear(): void {
    completions.value = new Map();
    loadedDates.value = new Set();
  }

  // ── Loaders ────────────────────────────────────────────────────────
  /**
   * Batch: fetch existing rows for many children on one date in a single query.
   * Children without an existing row stay absent from the cache — they'll be
   * created lazily on first interaction via loadOne (which falls back to
   * getOrCreateDailyCompletion).
   */
  async function loadForChildren(childIds: string[], date: string): Promise<void> {
    if (!childIds.length) {
      const next = new Set(loadedDates.value);
      next.add(date);
      loadedDates.value = next;
      return;
    }

    loading.value = true;
    try {
      const rows = await dailyCompletionService.getDailyCompletionsForChildren(childIds, date);
      hydrateMany(rows.values());
      const next = new Set(loadedDates.value);
      next.add(date);
      loadedDates.value = next;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Single: fetch one (child, date) and hydrate. Falls through to
   * getOrCreateDailyCompletion so reconciliation + creation still happen.
   * Use this when a screen needs a row that wasn't in the batch (e.g. user
   * navigated to a new date, or we need to ensure the row exists before mutating).
   */
  async function loadOne(childId: string, date: string): Promise<any | null> {
    const completion = await dailyCompletionService.getOrCreateDailyCompletion(childId, date);
    if (completion) hydrate(completion);
    return completion;
  }

  return {
    // state
    completions,
    loadedDates,
    loading,
    // getters
    get,
    has,
    isDateLoaded,
    // mutations
    hydrate,
    hydrateMany,
    remove,
    clear,
    // loaders
    loadForChildren,
    loadOne,
  };
});
